// src/utils/servicerequestsPermissions.js

/**
 * Tarkistaa onko käyttäjä etuoikeutettu (hallitus / isännöitsijä / admin)
 * valitussa yhtiössä.
 * @param {object} user - UsersContextin user-olio
 * @param {string} companyId
 * @returns {boolean}
 */
export function isPrivileged(user, companyId) {
  const roles = user?.userRoles || [];
  return roles.some(
    (r) =>
      r.companyId === companyId &&
      (r.role === "hallitus" || r.role === "isännöitsijä" || r.role === "admin")
  );
}

/**
 * Laskee kaikki Servicerequest-näkymässä käytettävät oikeusliput yhdestä paikasta.
 * Tämän avulla UI pysyy ohuena ja logiikka keskitettynä.
 *
 * @param {object} params
 * @param {object} params.user - UsersContextin user-olio
 * @param {string} params.companyId
 * @param {object|null} params.request - aktiivinen pyyntö (voi olla null)
 */
export function computePermissions({ user, companyId, request }) {
  const privileged = isPrivileged(user, companyId);
  const isCreator =
    !!request?.createdBy?.uid && !!user?.uid && request.createdBy.uid === user.uid;

  // Säännöt v1 (sovitut):
  // - Tila & prioriteetti: vain privileged
  // - Asunto: privileged tai (creator && request.apartmentId on tyhjä)
  // - Sisäiset viestit: vain privileged
  // - Liitteet: kaikki voivat lisätä/poistaa (v1—yksinkertaistus)
  const canEditType = privileged;
  const canEditStatus = privileged;
  const canEditPriority = privileged;
  const canEditApartment = privileged || (isCreator && !request?.apartmentId);
  const canWriteInternal = privileged;
  const canManageAttachments = true;

  // Pikanapit statusmuutoksille
  const status = request?.status || "uusi";
  const canTakeIn = privileged && status === "uusi";
  const canClose = privileged && status !== "suljettu" && status !== "uusi";
  const canReopen = privileged && status === "suljettu";

  return {
    privileged,
    isCreator,
    canEditType,
    canEditStatus,
    canEditPriority,
    canEditApartment,
    canWriteInternal,
    canManageAttachments,
    canTakeIn,
    canClose,
    canReopen,
  };
}

/**
 * Suodattaa ja lajittelee viestit näkyvyys-/roolisääntöjen mukaisesti.
 * @param {Array} messages
 * @param {{ privileged: boolean }} opts
 * @returns {Array} uusin ensin
 */
export function filterVisibleMessages(messages, { privileged }) {
  const arr = Array.isArray(messages) ? messages : [];
  const filtered = privileged
    ? arr
    : arr.filter((m) => (m.visibility || "public") === "public");
  // Uusin ensin
  return [...filtered].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}
